import { useState } from "react";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { colors } from "brand";
import { TermReasonChip } from "common-fe";
import {
  isScoreSuppressed,
  SUPPRESSED_SCORE_DISPLAY,
} from "score-common";
import { RIDE_LOOKUP_KIND, classifyRideForLookup } from "../utils/lookupColumn";
import { isResultRevealed } from "../utils/orderOfGo";
import { formatComposite } from "../utils/formatComposite";
import { formatTime } from "../utils/formatTime";
import { termChipReason, termChipLabels } from "../utils/termChip";
import SectionTag from "./SectionTag";
import RideDetailBlock, { hasRideDetail } from "./RideDetailBlock";

// #3394: the right-hand cell of a row is one of: the score, IN RING (green),
// the ride time (a ride still to go), or "pending" (ridden, score not yet
// revealed). A scratch shows nothing here: the exception label says Scratched.
function ScoreCell({ row, scratched, isCurrent, result, t }) {
  const classified = classifyRideForLookup({ status: row.status, isCurrent, result });
  const { composite } = classified;
  let { kind } = classified;
  // The retired Results box showed any revealed result whatever the ride row's
  // status said, so a revealed score outranks the "ride time" cell.
  if (kind === RIDE_LOOKUP_KIND.DASH && result && isResultRevealed(result.status)) {
    kind = RIDE_LOOKUP_KIND.SCORE;
  }
  const shownComposite = kind === RIDE_LOOKUP_KIND.SCORE && composite == null ? result?.composite : composite;
  if (scratched || kind === RIDE_LOOKUP_KIND.SCRATCHED) return null;
  if (isCurrent) {
    return (
      <Typography
        variant="body2"
        data-testid="lookup-row-in-ring"
        sx={{ color: "success.dark", fontWeight: 800, whiteSpace: "nowrap" }}
      >
        {t("lookupColumn.inRing")}
      </Typography>
    );
  }
  if (kind === RIDE_LOOKUP_KIND.DASH) {
    const time = formatTime(row.scheduledTime, row.scheduledTimeZone);
    return (
      <Typography variant="body2" color="text.secondary" data-testid="lookup-row-time" sx={{ whiteSpace: "nowrap" }}>
        {time || t("results.unplaced")}
      </Typography>
    );
  }
  if (kind === RIDE_LOOKUP_KIND.PENDING) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("lookupColumn.pending")}
      </Typography>
    );
  }
  // Same gate as the retired Results box (#2840): an eliminated ride's real 0
  // is never shown as a score.
  if (isScoreSuppressed(row.termReason)) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="lookup-row-score">
        {SUPPRESSED_SCORE_DISPLAY}
      </Typography>
    );
  }
  // A place-only sheet (DSHB, #2183 AC5) publishes a placing but no composite:
  // the place cell carries it, and formatComposite(null) must never print a
  // bare "%".
  if (!Number.isFinite(shownComposite)) return null;
  return (
    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }} data-testid="lookup-row-score">
      {t("results.composite", { value: formatComposite(shownComposite) })}
    </Typography>
  );
}

// #3395 -- the open row's detail is the shared RideDetailBlock (the same
// component the In the Ring Now box uses, card 3397), indented to start under
// the rider name column. The offset is the summary's own left edge: padding
// (px 1.5) + place cell + gap + bib cell + gap, so it is built from the same
// PLACE_WIDTH / BIB_WIDTH the cells use and cannot drift from them.
const PLACE_WIDTH = 28;
const BIB_WIDTH = 36;
const SUMMARY_GAP = 1;

function RideDetail({ row, t }) {
  return (
    <Box
      data-testid="lookup-ride-detail"
      sx={{
        pr: 1.5,
        pb: 1,
        pl: (theme) =>
          `calc(${theme.spacing(1.5)} + ${PLACE_WIDTH}px + ${BIB_WIDTH}px + ${theme.spacing(SUMMARY_GAP * 2)})`,
      }}
    >
      {hasRideDetail(row.detail) ? (
        <RideDetailBlock detail={row.detail} />
      ) : (
        <Typography data-testid="detail-empty" variant="body2" color="text.secondary">
          {t("detail.empty")}
        </Typography>
      )}
    </Box>
  );
}

// The place cell: an orange official placing, a grey unofficial rank, or empty.
function PlaceCell({ place, placeKind, placedList, t }) {
  if (place == null) {
    return (
      <Typography
        data-testid="lookup-row-place"
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: PLACE_WIDTH, flexShrink: 0, fontWeight: 800 }}
      >
        {placedList ? t("results.unplaced") : ""}
      </Typography>
    );
  }
  return (
    <Typography
      data-testid="lookup-row-place"
      data-place-kind={placeKind}
      variant="body1"
      sx={{
        minWidth: 28,
        flexShrink: 0,
        fontWeight: 800,
        color: placeKind === "placing" ? colors.foxOrange : "text.secondary",
      }}
    >
      {place}
    </Typography>
  );
}

// One row of the merged list: place/rank, bib, rider over horse, score / IN
// RING / ride time, chevron. Opens like an accordion; the open body is
// the shared ride-detail block (#3395).
export default function LookupRideRow({ entry, placedList, t }) {
  const { row, result, isCurrent, place, placeKind, scratched } = entry;
  const [expanded, setExpanded] = useState(false);
  // #3399 -- a terminated ride shows a red term-reason chip, never a strikethrough.
  const chipReason = termChipReason(row, scratched);
  return (
    <Box
      data-testid="lookup-ride-row"
      data-ride-id={row.id}
      data-current={isCurrent ? "true" : "false"}
      data-expanded={expanded ? "true" : "false"}
      data-place={place ?? undefined}
      data-place-kind={placeKind ?? undefined}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: isCurrent ? (theme) => alpha(theme.palette.success.main, 0.14) : "transparent",
      }}
    >
      {/* A scratch carries no action (RideRow's old rule): no expand control. */}
      <ButtonBase
        component="div"
        data-testid="lookup-ride-summary"
        disabled={scratched}
        aria-expanded={scratched ? undefined : expanded}
        onClick={() => {
          if (!scratched) setExpanded((v) => !v);
        }}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "flex-start",
          textAlign: "left",
          gap: SUMMARY_GAP,
          px: 1.5,
          py: 0.75,
        }}
      >
        <PlaceCell place={scratched ? null : place} placeKind={placeKind} placedList={placedList && !scratched} t={t} />
        <Typography
          data-testid="lookup-row-bib"
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: BIB_WIDTH, flexShrink: 0, fontWeight: 600 }}
        >
          {row.competitorNumber}
        </Typography>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {row.riderName}
            <SectionTag section={row.section} />
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {row.horseName}
          </Typography>
          {chipReason && (
            <TermReasonChip
              data-testid="lookup-row-exception"
              termReason={chipReason}
              termLabels={termChipLabels(t)}
              sx={{ display: "flex", width: "fit-content", mt: 0.25, fontWeight: 700 }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <ScoreCell row={row} scratched={scratched} isCurrent={isCurrent} result={result} t={t} />
          {scratched ? (
            <Box sx={{ width: 20, flexShrink: 0 }} />
          ) : (
            <ExpandMoreIcon
              data-testid="lookup-ride-expand-icon"
              fontSize="small"
              sx={{ color: "text.secondary", transform: expanded ? "rotate(180deg)" : "none" }}
            />
          )}
        </Box>
      </ButtonBase>
      {expanded && !scratched && <RideDetail row={row} t={t} />}
    </Box>
  );
}
